import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Eyebrow } from "@/components/chrome";
import { TESTIMONIALS } from "@/data/testimonials";

const PER_PAGE = 3;

export function ReviewsWall() {
  const pages = Math.ceil(TESTIMONIALS.length / PER_PAGE);
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPage((p) => (p + 1) % pages), 5200);
    return () => clearInterval(t);
  }, [pages]);

  const visible = TESTIMONIALS.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  return (
    <section className="mx-auto max-w-7xl px-5 py-20 sm:py-28" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Reviews</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Loved from Victoria to St. John's
          </h2>
        </div>
        <div className="flex items-center gap-2" data-testid="reviews-rating-badge">
          <span className="flex gap-0.5 text-amber-500">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
          </span>
          <span className="text-sm font-semibold text-slate-900">4.9</span>
          <span className="text-sm text-slate-500">· 286 verified client reviews</span>
        </div>
      </div>

      <div className="mt-12 min-h-[240px]">
        <AnimatePresence mode="wait">
          <motion.div key={page} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-5 md:grid-cols-3" data-testid="reviews-wall">
            {visible.map((r, i) => (
              <figure key={r.name} data-testid={`review-card-${i}`}
                className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7">
                <Quote className="h-6 w-6 text-cyan-200" />
                <blockquote className="mt-4 flex-1 leading-relaxed text-slate-700">"{r.text}"</blockquote>
                <figcaption className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex gap-0.5 text-amber-500">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-current" />)}
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.service} · {r.city}</p>
                </figcaption>
              </figure>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-8 flex gap-2" data-testid="reviews-dots">
        {[...Array(pages)].map((_, i) => (
          <button key={i} type="button" aria-label={`Reviews page ${i + 1}`} data-testid={`reviews-dot-${i}`}
            onClick={() => setPage(i)}
            className={`h-2 rounded-full transition-all duration-300 ${i === page ? "w-8 bg-cyan-600" : "w-2 bg-slate-300 hover:bg-slate-400"}`} />
        ))}
      </div>
    </section>
  );
}
